/**
 * useRealtimePermissionChecker - Real-time permission checking hook
 * 
 * This hook replaces the old usePermissionChecker with real-time capabilities:
 * - Always fetches fresh permission data
 * - Smart caching with TTL
 * - Auto-refresh when permissions change
 * - Manual refresh capability
 */

import { useMemo } from 'react';
import { useRealtimePermissions } from './useRealtimePermissions';
import { 
  PermissionManager, 
  createPermissionManager, 
  PermissionContext,
  PermissionResource,
  PermissionAction,
  PERMISSION_RESOURCES,
  PERMISSION_ACTIONS
} from '@/__core/permissions/utils/PermissionManager';

export interface UseRealtimePermissionCheckerReturn {
  // Core permission checking methods
  hasPermission: (resource: string, action: string) => boolean;
  canRead: (resource: string) => boolean;
  canCreate: (resource: string) => boolean;
  canUpdate: (resource: string) => boolean;
  canDelete: (resource: string) => boolean;
  
  // Role checking
  hasRole: (roleName: string) => boolean;
  
  // Permission summary
  getPermissionSummary: (resource: string) => {
    canRead: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
  };
  
  // Resource permissions
  getResourcePermissions: (resource: string) => string[];
  
  // Multiple permissions checking
  hasAnyPermission: (permissions: Array<{resource: string, action: string}>) => boolean;
  hasAllPermissions: (permissions: Array<{resource: string, action: string}>) => boolean;
  
  // Permission manager instance
  permissionManager: PermissionManager;
  
  // Real-time features
  refreshPermissions: () => void;
  isLoading: boolean;
  lastUpdated: number;
}

export const useRealtimePermissionChecker = (): UseRealtimePermissionCheckerReturn => {
  const { permissionContext, refreshPermissions, isLoading, lastUpdated } = useRealtimePermissions();
  
  // Create permission manager instance with real-time context
  const permissionManager = useMemo(() => {
    const context: PermissionContext = {
      user: permissionContext.user,
      globalRoles: permissionContext.globalRoles,
      selectedCompany: permissionContext.selectedCompany
    };
    return createPermissionManager(context);
  }, [permissionContext]);
  
  // Memoized permission checking methods
  const hasPermission = useMemo(() => {
    return (resource: string, action: string) => {
      return permissionManager.hasPermission(resource, action);
    };
  }, [permissionManager]);
  
  const canRead = useMemo(() => {
    return (resource: string) => {
      return permissionManager.canRead(resource);
    };
  }, [permissionManager]);
  
  const canCreate = useMemo(() => {
    return (resource: string) => {
      return permissionManager.canCreate(resource);
    };
  }, [permissionManager]);
  
  const canUpdate = useMemo(() => {
    return (resource: string) => {
      return permissionManager.canUpdate(resource);
    };
  }, [permissionManager]);
  
  const canDelete = useMemo(() => {
    return (resource: string) => {
      return permissionManager.canDelete(resource);
    };
  }, [permissionManager]);
  
  const hasRole = useMemo(() => {
    return (roleName: string) => {
      return permissionManager.hasRole(roleName);
    };
  }, [permissionManager]);
  
  const getPermissionSummary = useMemo(() => {
    return (resource: string) => {
      return permissionManager.getPermissionSummary(resource);
    };
  }, [permissionManager]);
  
  const getResourcePermissions = useMemo(() => {
    return (resource: string) => {
      return permissionManager.getResourcePermissions(resource);
    };
  }, [permissionManager]);
  
  const hasAnyPermission = useMemo(() => {
    return (permissions: Array<{resource: string, action: string}>) => {
      return permissionManager.hasAnyPermission(permissions);
    };
  }, [permissionManager]);
  
  const hasAllPermissions = useMemo(() => {
    return (permissions: Array<{resource: string, action: string}>) => {
      return permissionManager.hasAllPermissions(permissions);
    };
  }, [permissionManager]);
  
  return {
    hasPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    hasRole,
    getPermissionSummary,
    getResourcePermissions,
    hasAnyPermission,
    hasAllPermissions,
    permissionManager,
    refreshPermissions,
    isLoading,
    lastUpdated
  };
};

// Convenience hooks for specific resources with real-time capabilities
export const useRealtimeUsersPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, refreshPermissions, isLoading, lastUpdated } = useRealtimePermissionChecker();
  
  return useMemo(() => ({
    canRead: canRead(PERMISSION_RESOURCES.USERS),
    canCreate: canCreate(PERMISSION_RESOURCES.USERS),
    canUpdate: canUpdate(PERMISSION_RESOURCES.USERS),
    canDelete: canDelete(PERMISSION_RESOURCES.USERS),
    summary: getPermissionSummary(PERMISSION_RESOURCES.USERS),
    refreshPermissions,
    isLoading,
    lastUpdated
  }), [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, refreshPermissions, isLoading, lastUpdated]);
};

export const useRealtimeRolesPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, refreshPermissions, isLoading, lastUpdated } = useRealtimePermissionChecker();
  
  return useMemo(() => ({
    canRead: canRead(PERMISSION_RESOURCES.ROLES),
    canCreate: canCreate(PERMISSION_RESOURCES.ROLES),
    canUpdate: canUpdate(PERMISSION_RESOURCES.ROLES),
    canDelete: canDelete(PERMISSION_RESOURCES.ROLES),
    summary: getPermissionSummary(PERMISSION_RESOURCES.ROLES),
    refreshPermissions,
    isLoading,
    lastUpdated
  }), [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, refreshPermissions, isLoading, lastUpdated]);
};

export const useRealtimeCompaniesPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, refreshPermissions, isLoading, lastUpdated } = useRealtimePermissionChecker();
  
  return useMemo(() => ({
    canRead: canRead(PERMISSION_RESOURCES.COMPANIES),
    canCreate: canCreate(PERMISSION_RESOURCES.COMPANIES),
    canUpdate: canUpdate(PERMISSION_RESOURCES.COMPANIES),
    canDelete: canDelete(PERMISSION_RESOURCES.COMPANIES),
    summary: getPermissionSummary(PERMISSION_RESOURCES.COMPANIES),
    refreshPermissions,
    isLoading,
    lastUpdated
  }), [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, refreshPermissions, isLoading, lastUpdated]);
};

export const useRealtimeEmployeesPermissions = () => {
  const { canRead, canCreate, canUpdate, canDelete, getPermissionSummary, refreshPermissions, isLoading, lastUpdated } = useRealtimePermissionChecker();
  
  return useMemo(() => ({
    canRead: canRead(PERMISSION_RESOURCES.EMPLOYEES),
    canCreate: canCreate(PERMISSION_RESOURCES.EMPLOYEES),
    canUpdate: canUpdate(PERMISSION_RESOURCES.EMPLOYEES),
    canDelete: canDelete(PERMISSION_RESOURCES.EMPLOYEES),
    summary: getPermissionSummary(PERMISSION_RESOURCES.EMPLOYEES),
    refreshPermissions,
    isLoading,
    lastUpdated
  }), [canRead, canCreate, canUpdate, canDelete, getPermissionSummary, refreshPermissions, isLoading, lastUpdated]);
};

// Export permission constants for easy access
export { PERMISSION_RESOURCES, PERMISSION_ACTIONS };
export type { PermissionResource, PermissionAction };
